import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * <mobli-cards> — the term switcher over a grid of product cards.
 *
 * Rental products price per term, so a card has no single price: it has one per
 * plazo. Picking a term here repoints every card in the grid at that term's
 * "Mensual" variant — the price shown and the variant the add button sends stay
 * the same object, so what the shopper reads is what the cart charges.
 *
 * Prices come pre-formatted from Liquid's `money` filter. Terms a product does
 * not offer fall back to its longest available one, which is why a 6-month grid
 * can still show a piece whose minimum is 12.
 */
class MobliCards extends HTMLElement {
  connectedCallback() {
    this.term = this.dataset.term || '12';
    this.addEventListener('click', this.#onClick);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const pill = event.target.closest('[data-term-option]');
    if (pill) {
      this.term = pill.dataset.termOption;
      this.#render();
      return;
    }

    const add = event.target.closest('[data-card-add]');
    if (add) this.#add(add);
  };

  /** @returns {Array<{card: Element, terms: object}>} */
  get #cards() {
    return [...this.querySelectorAll('[data-mobli-card]')].map((card) => {
      const payload = card.querySelector('[data-card-terms]');
      let terms = {};
      try {
        terms = JSON.parse(payload?.textContent || '{}');
      } catch {
        terms = {};
      }
      delete terms._;
      return { card, terms };
    });
  }

  /** The requested term, or the closest one the product actually offers. */
  #resolve(terms) {
    if (terms[this.term]) return { months: this.term, entry: terms[this.term] };

    const months = Object.keys(terms)
      .map(Number)
      .sort((a, b) => a - b);
    if (!months.length) return null;

    // Prefer the next term up — a longer plazo is cheaper, never a worse offer.
    const wanted = Number(this.term);
    const next = months.find((m) => m > wanted) ?? months[months.length - 1];
    return { months: String(next), entry: terms[String(next)] };
  }

  #render() {
    for (const node of this.querySelectorAll('[data-term-option]')) {
      node.setAttribute('aria-checked', String(node.dataset.termOption === this.term));
    }

    for (const { card, terms } of this.#cards) {
      const resolved = this.#resolve(terms);
      if (!resolved) continue;

      const price = card.querySelector('[data-card-price]');
      if (price) price.textContent = resolved.entry.price;

      const label = card.querySelector('[data-card-term]');
      if (label) label.textContent = resolved.months;

      const button = card.querySelector('[data-card-add]');
      if (button) {
        button.dataset.variantId = resolved.entry.id;
        button.disabled = !resolved.entry.available;
      }
    }
  }

  async #add(button) {
    if (button.dataset.busy === 'true') return;

    const id = button.dataset.variantId;
    const original = button.dataset.label || button.textContent;
    button.dataset.label = original;

    if (!id) return this.#fail(button, 'Sin variante para este plazo');

    button.dataset.busy = 'true';
    button.textContent = 'Añadiendo…';

    // The cart icon and drawer do not act on the event itself — they await the
    // promise it carries (`event.promise?.then(...)`). Dispatching without one
    // makes the optional chain short-circuit, so the line reached the cart but
    // nothing on screen heard about it until the next page load. The promise is
    // created before the request and resolved with the refreshed cart.
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          // The drawer re-renders from these rather than refetching the page.
          sections: this.#cartSectionIds(),
        }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        // Shopify explains the refusal in `description` — an unpublished
        // product or a sold-out variant reads very differently from a network
        // failure, and the shopper deserves to know which it was.
        const reason = payload?.description || payload?.message || `Error ${response.status}`;
        throw new Error(reason);
      }

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mobli-cards',
          sourceId: this.id || 'mobli-cards',
          itemCount: 1,
          sections: payload?.sections,
          didError: false,
        },
      });

      button.textContent = 'Añadido ✓';
      this.#reset(button);
    } catch (error) {
      console.error('[mobli-cards] no se pudo añadir', { variantId: id, error });
      // Settle the promise either way: a listener left waiting on it would
      // never update again, not even on the next successful add.
      deferred.reject(error);
      this.#fail(button, error.message);
    }
  }

  /** The cart sections the drawer needs back so it can re-render itself. */
  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((node) => node instanceof HTMLElement && node.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  async #fetchCart() {
    const component = document.querySelector('cart-items-component');
    if (component) {
      await customElements.whenDefined('cart-items-component');
      // @ts-ignore — the component owns its own fetch, and using it keeps its
      // internal state in step with what we just read.
      if (typeof component.fetchCartData === 'function') return component.fetchCartData();
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`No se pudo leer el carrito: ${response.status}`);
    return response.json();
  }

  #fail(button, message) {
    button.textContent = message.length > 34 ? 'No se pudo añadir' : message;
    button.title = message;
    this.#reset(button);
  }

  #reset(button) {
    setTimeout(() => {
      button.textContent = button.dataset.label;
      button.removeAttribute('title');
      button.dataset.busy = 'false';
    }, 2200);
  }
}

if (!customElements.get('mobli-cards')) {
  customElements.define('mobli-cards', MobliCards);
}
